/**
 * #2030 — reading an employee custom field without taking the page down with it.
 *
 * ══ THE INCIDENT ══
 *
 * Local 24, 2026-10-02: the phone sheet for job 330041 "Mrs. Doubtfire" would not
 * render. The API answered 200. ONE employee out of 750 held
 *
 *     {"name": "Home Local", "value": "{"name":"D Notes","value":""}"}
 *
 * in `custom_field1` — a whole custom-field object nested inside `value` with its
 * quotes unescaped, so the string is not JSON at all. `getCustomField()` called
 * `JSON.parse` on it with no try/catch, the SyntaxError escaped the render, and the
 * sheet blanked FOR EVERYONE. The workaround on the phone was to untick the
 * custom-field columns in Owner Options, which stops the sheet calling this at all.
 *
 * ══ WHY A MODULE ══
 *
 * `getCustomField` was copied into seven places with three different behaviours:
 *
 *   phoneSheet.vue · dynamicColumns.vue · DailyScheduleAllEmployeesDynamicColumns.vue
 *   dynamicColumns911Availability.vue · editEmployeePersonal.vue      — NO try/catch
 *   employeeList.vue · Employee911Availability.vue                    — try/catch
 *
 * and the two guarded copies compute their nesting re-check against the OUTER value
 * (`customFld.value.search(...)`, which always matches) where phoneSheet checks the
 * INNER one — so a legitimately nested value rendered as the inner value on the phone
 * sheet and as blank in the employee list. One module, one behaviour.
 *
 * ══ NEVER THROW, NEVER SWALLOW SILENTLY ══
 *
 * `parseCustomField` has no throwing path. A value it cannot read comes back
 * `ok: false` with `raw` intact, and the caller shows the raw text — the user sees
 * something is wrong with THAT cell instead of losing the page. `warnOnce` keeps the
 * console to one line per distinct bad value, because the failing render is a table
 * and the alternative is 750 identical warnings.
 *
 * The write side is `app/Support/CustomField.php`; this file only ever READS.
 */

/** A custom field as the renderer wants it. */
function field(ok, name, value, raw) {
  return { ok, name, value, raw };
}

/** Values that mean "nothing here" once they have been through the old writers. */
const EMPTY_SENTINELS = ['{}', 'NULL', 'null', 'Null', 'undefined'];

const warned = new Set();

/**
 * One console line per distinct bad value. A broken field is one row of a table
 * rendered for every employee, so the un-deduplicated version is 750 lines.
 */
export function warnOnce(raw) {
  const key = String(raw);
  if (warned.has(key)) return false;
  warned.add(key);
  // eslint-disable-next-line no-console
  console.warn('[#2030] custom field is not valid JSON, showing it raw:', key);

  return true;
}

/** Test seam. Nothing in the app calls this. */
export function resetWarnings() {
  warned.clear();
}

/**
 * True when `s` looks like a custom-field object serialised into a string.
 *
 * The seven old copies tested `value.search('{"name":')` — no space after the colon.
 * That misses `ownerOptions`' own output (`{"name": "x", "value": "y"}`, spaced), so a
 * value nested by THAT builder read as plain text and rendered as a JSON blob. The
 * pattern is whitespace-tolerant here and in `app/Support/CustomField.php`; this is a
 * deliberate widening of the old behaviour.
 */
const NESTED = /\{\s*"name"\s*:/;

function looksNested(s) {
  return typeof s === 'string' && NESTED.test(s);
}

function isEmptySentinel(s) {
  return typeof s === 'string' && EMPTY_SENTINELS.indexOf(s) >= 0;
}

/**
 * Read a stored `custom_field1`/`custom_field2`. NEVER THROWS.
 *
 * Unwraps exactly ONE level of nesting, which is the shape the old writers could
 * produce (`editEmployee.vue` loaded the raw JSON into the text input, then
 * re-stringified it around itself). Two levels is not a shape anything created, and
 * unwrapping without limit would turn a hostile value into a loop.
 *
 * @param {*} raw  whatever the API returned — a JSON string, '', null, already-parsed
 * @returns {{ok: boolean, name: string, value: string, raw: *}}
 *   ok:false means the value could not be read; `raw` is then what to display.
 */
export function parseCustomField(raw) {
  if (raw === null || raw === undefined || raw === '') return field(true, '', '', raw);

  // Already an object: some callers hand us a decoded row.
  let outer = raw;
  if (typeof raw === 'string') {
    try {
      outer = JSON.parse(raw);
    } catch (e) {
      warnOnce(raw);
      return field(false, '', '', raw);
    }
  }

  if (!outer || typeof outer !== 'object') return field(true, '', '', raw);

  const name = typeof outer.name === 'string' ? outer.name : '';
  let value = outer.value;

  if (typeof value !== 'string') {
    // A number is a legitimate value typed into a text box; anything else is nothing.
    value = typeof value === 'number' ? String(value) : '';

    return field(true, name, value, raw);
  }

  if (looksNested(value)) {
    let inner;
    try {
      inner = JSON.parse(value);
    } catch (e) {
      // THE INCIDENT VALUE LANDS HERE. The outer object parsed (it would not have, in
      // the 2026-10-02 case — but a half-escaped variant can), the inner one cannot.
      warnOnce(raw);

      return field(false, name, '', raw);
    }
    const innerValue = inner && typeof inner.value === 'string' ? inner.value : '';
    // One level only: a value still nested after unwrapping is not shown as data.
    if (looksNested(innerValue) || isEmptySentinel(innerValue)) {
      return field(true, name, '', raw);
    }

    return field(true, name, innerValue, raw);
  }

  if (isEmptySentinel(value)) return field(true, name, '', raw);

  return field(true, name, value, raw);
}

/**
 * What to put in the cell.
 *
 * `placeholder` is per-call because the call sites disagree and always have: the
 * phone sheet and the availability columns show `--`, the employee list shows ''.
 * Preserving that is deliberate — this change is about not throwing, not about
 * re-styling seven screens.
 *
 * An unreadable value shows its RAW text. Ed's ask: "on bad JSON show the raw text
 * or blank and log once" — raw, because a cell reading `{"name": "Home Local", …` is
 * a findable defect where a blank cell is indistinguishable from an empty field.
 */
export function customFieldValue(raw, placeholder) {
  const ph = placeholder === undefined ? '' : placeholder;
  const parsed = parseCustomField(raw);

  if (!parsed.ok) return String(parsed.raw === null || parsed.raw === undefined ? ph : parsed.raw);

  return parsed.value === '' ? ph : parsed.value;
}

/** The stored label, for a screen that shows the field's own name. */
export function customFieldName(raw) {
  return parseCustomField(raw).name;
}

/** True when the stored value is unreadable — what the audit report counts. */
export function isMalformedCustomField(raw) {
  return !parseCustomField(raw).ok;
}

export default {
  parseCustomField,
  customFieldValue,
  customFieldName,
  isMalformedCustomField,
  warnOnce,
  resetWarnings,
};
